import { useCallback, useEffect, useMemo, useState } from 'react'

import { FixedBanner, Radio, SkeletonBox } from '@circlefin/components'
import { useForm } from '@circlefin/form'
import { useDebounce, useMoney } from '@circlefin/hooks'
import { BankAccounts } from '@features/bank-account/forms/Combobox'
import { useCurrentCurrency } from '@features/locales/hooks/currency'
import {
  isBankAccountType,
  isRailType,
} from '@services/type-guards/BankAccount'
import { Center } from '@shared/components/layout'
import {
  Currency,
  FxQuoteType,
  useCircleWalletsQuery,
  useEntityLimitsQuery,
  WireTransferTypeEnum,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { FxAgreement } from '../../../components/FxAgreement/FxAgreement'
import { useGetBankAccounts } from '../../../hooks/bank-accounts/useGetBankAccounts'
import { useTransferWithFx } from '../../../hooks/fx'
import {
  getDefaultRail,
  getEligibleRails,
  isSameCurrency,
  getCurrenciesAndDefault,
  getFullSchema,
  getDefaultCurrencyOptions,
} from '../../../hooks/fx/helpers'

import type { FxFormValues } from '../../../hooks/fx/helpers'
import type { IconProps } from '@circlefin/components/lib/Icon'
import type {
  BankAccount,
  BankAccountType,
  GetFxQuoteInput,
  RailType,
} from '@shared/graphql'

export interface FxTransferFormProps {
  /**
   * The flow we are on, either deposit or withdrawal.
   */
  mode: WireTransferTypeEnum
  /**
   * The currency that the user would like to receive or redeem. Fixed based on prior selection.
   */
  fixedCurrency: Currency
  /**
   * On Form Submit.
   */
  onSubmit: (formValues: FxFormValues) => void
}

const CURRENCY_ICONS_MAP: Map<Currency, IconProps['name']> = new Map([
  [Currency.SGD, 'SGP'],
  [Currency.USDC, 'USDC'],
  [Currency.USD, 'USA'],
  [Currency.BRL, 'BR'],
  [Currency.MXN, 'MXN'],
  [Currency.EUR, 'EU'],
  [Currency.EURC, 'EURC'],
])

/**
 * Form for handling FX transfers (deposit/withdrawal).
 * Flow:
 * 1. User selects account
 * 2. Rails are determined based on account type
 * 3. Available currencies are set based on rail
 * 4. Quote is fetched when amount/currency changes (debounced).
 */
export const FxTransferForm: React.FC<FxTransferFormProps> = ({
  onSubmit,
  fixedCurrency,
  mode,
}) => {
  const { t } = useTranslation('modals.transfer')
  const { money } = useMoney()
  const [, { tokenToCurrency }] = useCurrentCurrency()

  const [fiatCurrencies, setFiatCurrencies] = useState<Currency[]>(
    getDefaultCurrencyOptions(fixedCurrency),
  )

  const [getBankAccount, { loading: bankLoading, error: bankError }] =
    useGetBankAccounts()

  const {
    data: balanceData,
    loading: balanceLoading,
    error: balanceError,
  } = useCircleWalletsQuery({
    fetchPolicy: 'cache-and-network',
  })

  const balance = useMemo(() => {
    return balanceData?.circleWallets.find(
      (bal) => bal.balance.currency === fixedCurrency,
    )?.balance.amount
  }, [balanceData?.circleWallets, fixedCurrency])

  const {
    data: limitData,
    loading: limitLoading,
    error: limitError,
  } = useEntityLimitsQuery({
    variables: {
      currency: fixedCurrency,
      type: mode,
    },
  })

  const {
    refetch: refetchQuote,
    loading: quoteLoading,
    error: quoteError,
  } = useTransferWithFx()

  // Combine errors to make it easier to loop through for error banners
  const combinedErrors = useMemo(
    () => ({ bankError, limitError, balanceError, quoteError }),
    [bankError, limitError, balanceError, quoteError],
  )

  const loading = bankLoading || limitLoading || balanceLoading || quoteLoading

  const isDepositMode = mode === WireTransferTypeEnum.deposit

  /* Currency dropdowns - fixed currency is locked, fiat currencies change based on rail */
  const fixedCurrencyDropdownItems = useMemo(
    () => [
      {
        label: fixedCurrency,
        value: fixedCurrency,
        icon: CURRENCY_ICONS_MAP.get(fixedCurrency),
      },
    ],
    [fixedCurrency],
  )

  const newFiatCurrencyDropdownItems = useMemo(
    () =>
      fiatCurrencies.map((currency) => ({
        label: currency,
        value: currency,
        icon: CURRENCY_ICONS_MAP.get(currency),
      })),
    [fiatCurrencies],
  )

  const [Form, { watch, setValue }] = useForm<FxFormValues>({
    schema: useMemo(
      () =>
        getFullSchema({
          remainingLimit: limitData?.entityLimits?.available,
        }),
      [limitData?.entityLimits?.available],
    ),
    mode: 'onChange',
    defaultValues: {
      mode,
    },
  })
  const [account, fromCurrency, toCurrency, fromAmount, toAmount, rail] = watch(
    ['account', 'fromCurrency', 'toCurrency', 'fromAmount', 'toAmount', 'rail'],
  )

  // Tracks whether the selected currencies require FX action (IE its false when USD -> USDC or EUR -> EURC)
  const isFxQuote = !isSameCurrency(fromCurrency, toCurrency)

  // Wrapper for refetching the quote to update the form values
  const refetch = useCallback(
    async (input: GetFxQuoteInput) => {
      const quote = await refetchQuote({ ...input, type: FxQuoteType.internal })

      // If API call fails, still set currencies to avoid undefined values
      if (quote === undefined) {
        setValue('fromCurrency', input.from.currency)
        setValue('toCurrency', input.to.currency)

        return
      }

      // Set shouldValidate to true to trigger validation on the new values
      setValue('fromAmount', quote.from.amount, {
        shouldValidate: true,
      })
      setValue('toAmount', quote.to.amount, {
        shouldValidate: true,
      })
      setValue('fromCurrency', quote.from.currency)
      setValue('toCurrency', quote.to.currency)
    },
    [refetchQuote, setValue],
  )

  // Initial fetch of quote with default values
  useEffect(() => {
    void refetch({
      from: {
        currency:
          mode === WireTransferTypeEnum.withdrawal
            ? fixedCurrency
            : tokenToCurrency(fixedCurrency),
        amount: '0',
      },
      to: {
        currency:
          mode === WireTransferTypeEnum.deposit
            ? fixedCurrency
            : tokenToCurrency(fixedCurrency),
        amount: '0',
      },
    })
  }, [fixedCurrency, mode, refetch, tokenToCurrency])

  const withdrawalOverBalance = useMemo(() => {
    if (!balance || isDepositMode) return false

    const numberFromAmount = parseFloat(fromAmount)
    const numberBalance = parseFloat(balance)

    if (Number.isNaN(numberFromAmount) || Number.isNaN(numberBalance)) {
      return false
    }

    return numberFromAmount > numberBalance
  }, [balance, fromAmount, isDepositMode])

  // Get the set of rails that are available on the account that has been selected.
  const rails = useMemo(
    () =>
      getEligibleRails({
        bankAccount: account,
        currency: fixedCurrency,
      }),
    [account, fixedCurrency],
  )

  // Handles currency updates when account/rail changes
  const adjustCurrencies = useCallback(
    ({
      bankAccount,
      newRail,
    }: {
      bankAccount: BankAccount
      newRail: RailType | BankAccountType
    }) => {
      const newSupportedCurrencies = getCurrenciesAndDefault({
        bankAccount,
        newRail,
        currency: fixedCurrency,
      })

      setFiatCurrencies(newSupportedCurrencies.options)

      // Check whether we need to reset to default currency. This triggers when
      // the selected account doesn't support the currently selected currency.
      const resetValuesToDefault = !newSupportedCurrencies.options.includes(
        isDepositMode ? fromCurrency : toCurrency,
      )

      /* Refetch the quote with the new form values if we reset to default currency */
      if (resetValuesToDefault) {
        void refetch({
          from: isDepositMode
            ? { currency: newSupportedCurrencies.default } // deposit: we replaced fromCurrency
            : { currency: fromCurrency, amount: fromAmount },
          to: isDepositMode
            ? { currency: toCurrency, amount: toAmount }
            : { currency: newSupportedCurrencies.default }, // withdrawal: we replaced toCurrency
        })
      }
    },
    [
      fixedCurrency,
      fromAmount,
      fromCurrency,
      isDepositMode,
      refetch,
      toAmount,
      toCurrency,
    ],
  )

  const handleAccountChange = useCallback(
    async ({ value: selectedAccount }: { value?: BankAccount }) => {
      if (!selectedAccount) return

      const bankAccount = await getBankAccount(selectedAccount)

      if (!bankAccount) return

      const newRail = getDefaultRail({
        account: bankAccount,
        isDepositMode,
        currency: fixedCurrency,
      })

      // This should never happen, but is necessary for type safety
      if (newRail === '') return

      setValue('rail', newRail)
      setValue('account', bankAccount)

      adjustCurrencies({ bankAccount, newRail })
    },
    [adjustCurrencies, fixedCurrency, getBankAccount, isDepositMode, setValue],
  )

  const debouncedFetchQuote = useDebounce({
    callback: refetch,
    delay: 1500,
  })

  useEffect(() => {
    const { unsubscribe } = watch((fields, { name, type }) => {
      // Avoid refetching if value is manually set
      // Otherwise, we will get into an infinite loop
      if (type === undefined) return

      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
      switch (name) {
        case 'fromAmount':
          if (
            fields.fromAmount !== '' &&
            fields.fromCurrency != null &&
            fields.toCurrency != null
          ) {
            // eslint-disable-next-line sonarjs/void-use
            void debouncedFetchQuote({
              from: {
                amount: fields.fromAmount,
                currency: fields.fromCurrency,
              },
              to: { currency: fields.toCurrency },
            })
          }
          break
        case 'toAmount':
          if (
            fields.toAmount !== '' &&
            fields.fromCurrency != null &&
            fields.toCurrency != null
          ) {
            // eslint-disable-next-line sonarjs/void-use
            void debouncedFetchQuote({
              from: { currency: fields.fromCurrency },
              to: { amount: fields.toAmount, currency: fields.toCurrency },
            })
          }
          break
        case 'fromCurrency':
          if (
            fields.fromCurrency === undefined ||
            fields.toCurrency === undefined
          )
            return

          void refetch({
            from: { currency: fields.fromCurrency },
            to: { currency: fields.toCurrency, amount: fields.toAmount },
          })
          break
        case 'toCurrency':
          if (
            fields.fromCurrency === undefined ||
            fields.toCurrency === undefined
          )
            return

          void refetch({
            from: { currency: fields.fromCurrency, amount: fields.fromAmount },
            to: { currency: fields.toCurrency },
          })
          break
        case 'account':
          void handleAccountChange({ value: fields.account as BankAccount })
      }
    })

    return () => unsubscribe()
  }, [debouncedFetchQuote, handleAccountChange, refetch, watch])

  const handleRailChange = useCallback(
    (newRail: string | number) => {
      const newRailString = newRail.toString()

      // This should never happen, but is necessary for type safety
      if (
        (!isRailType(newRailString) && !isBankAccountType(newRailString)) ||
        !account
      )
        return

      setValue('rail', newRailString)

      adjustCurrencies({
        bankAccount: account,
        newRail: newRailString,
      })
    },
    [account, adjustCurrencies, setValue],
  )

  const submitDisabled =
    Object.values(combinedErrors).some(Boolean) ||
    withdrawalOverBalance ||
    loading

  return (
    <Form onSubmit={onSubmit}>
      <BankAccounts
        className="mb-6"
        disabled={loading}
        label={t`fx.selectAccount`}
        name="account"
      />
      {/* rail selection is only needed by transfer flow */}
      {!isDepositMode && !bankLoading && rails.length > 0 && (
        <Radio.Group
          className="mb-6"
          direction="vertical"
          label={t`fx.availableTransferMethods`}
          name="rail"
          onChange={handleRailChange}
          value={rail}
        >
          {rails.map((rail) => (
            <Radio key={rail} value={rail}>
              <span className="flex justify-between">
                <span>{t(`fx.rails.${rail}.label`)}</span>
                <span>{t(`fx.rails.${rail}.info`)}</span>
              </span>
            </Radio>
          ))}
        </Radio.Group>
      )}

      <div className="flex flex-row gap-2">
        <SkeletonBox className="mt-6 h-10 w-full" loading={loading}>
          <Form.MoneyInput
            className="w-full"
            currencyVariant={fromCurrency}
            data-testid="send-amount"
            disabled={!account}
            label={
              isDepositMode
                ? t`fx.send`
                : t`fx.withdraw.reviewDetails.withdrawAmountInput`
            }
            message={
              isDepositMode
                ? t`fx.chooseCorrespondingCurrency`
                : balance &&
                  t('fx.availableBalance', {
                    balance: money({
                      number: balance,
                      variant: fixedCurrency,
                    }),
                  })
            }
            name="fromAmount"
            placeholder="0.00"
            disableCurrencyVariant
          />
        </SkeletonBox>

        <SkeletonBox className="mt-6 h-10 w-40" loading={loading}>
          <Form.Dropdown
            className="mt-6 w-40"
            data-testid="send-currency"
            disabled={!isDepositMode || !account}
            items={
              isDepositMode
                ? newFiatCurrencyDropdownItems
                : fixedCurrencyDropdownItems
            }
            name="fromCurrency"
          />
        </SkeletonBox>
      </div>
      <div className="mt-6 flex flex-row gap-2">
        <SkeletonBox className="mt-6 h-10 w-full" loading={loading}>
          <Form.MoneyInput
            className="w-full"
            currencyVariant={toCurrency}
            data-testid="receive-amount"
            disabled={!account}
            label={t`fx.receive`}
            message={!isDepositMode && t`fx.chooseCorrespondingCurrency`}
            name="toAmount"
            placeholder="0.00"
            disableCurrencyVariant
          />
        </SkeletonBox>
        <SkeletonBox className="mt-6 h-10 w-36" loading={loading}>
          <Form.Dropdown
            className="mt-6 w-40"
            data-testid="receive-currency"
            disabled={isDepositMode || !account}
            items={
              isDepositMode
                ? fixedCurrencyDropdownItems
                : newFiatCurrencyDropdownItems
            }
            name="toCurrency"
          />
        </SkeletonBox>
      </div>

      {/* Show an error banner if something goes wrong fetching relevant values */}
      {Object.entries(combinedErrors).map(([key, value]) => (
        <FixedBanner
          key={key}
          className="mt-4"
          status="error"
          visible={Boolean(value)}
        >
          <FixedBanner.Title>
            {t(`fx.formError.${key}.title`)}
          </FixedBanner.Title>
          <FixedBanner.Description>
            {t(`fx.formError.${key}.description`)}
          </FixedBanner.Description>
        </FixedBanner>
      ))}

      {/* Show an error banner if the user is attempting to redeem more tokens than they have in their account. */}
      <FixedBanner
        className="mt-4"
        status="error"
        visible={withdrawalOverBalance}
      >
        <FixedBanner.Title>
          {t('fx.formError.withdrawalTooLarge.title')}
        </FixedBanner.Title>
        <FixedBanner.Description>
          {t('fx.formError.withdrawalTooLarge.description')}
        </FixedBanner.Description>
      </FixedBanner>

      {/* FX agreement is only needed by deposit flow and only if Non-USD -> USDC */}
      {isDepositMode && isFxQuote && (
        <Form.Checkbox
          className="mt-6"
          label={<FxAgreement mode="deposit" />}
          name="checkbox"
        />
      )}

      <Center className="pt-6" variant="horizontal">
        <Form.SubmitButton
          className="mt-6 w-64"
          data-testid="submit-button"
          disabled={submitDisabled}
          variant="primary"
        >
          {isDepositMode
            ? t`fx.viewDepositDetails`
            : t`fx.withdraw.reviewDetails.reviewDetails`}
        </Form.SubmitButton>
      </Center>
    </Form>
  )
}
